import { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '@uidotdev/usehooks';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { GET_USER_ACTIVITY, GET_MESSAGES, SEND_MESSAGE } from '~/services';
import { GET_USER } from '~/services/user';
import Bubbles from './components/Bubbles';
import LoadingIndicator from './components/LoadingIndicator';
import MessageInput from './components/MessageInput';
import {
  addFetchedMessages,
  setMessagesLoading,
  setMessagesError,
  setSendingMessage,
  setSendingError,
  selectMessagesByConversationId,
  selectMessagesLoading,
  selectIsSendingMessage,
  selectMessagesError,
  Message,
  addMessage,
} from '~/store/messages';
import { RootState, AppDispatch } from '~/store';

// Define types for API responses
interface GetMessagesResponse {
  response: {
    // Assuming API returns partial messages sometimes
    data: Partial<Message>[];
    next_cursor?: string | null;
  };
}

interface SendMessageResponse {
  send_message: {
    // Assuming API returns partial message
    data: {
      message: Message;
    };
    message: string;
    hasError: boolean;
  };
}

function Messages() {
  const { t } = useTranslation('locale');
  const [searchParams] = useSearchParams();
  const { agentId = '', personId } = useParams();
  const isSmallDevice = useMediaQuery('only screen and (max-width : 1024px)');
  const dispatch = useDispatch<AppDispatch>();
  const messageEndRef = useRef<HTMLDivElement>(null);

  const [localMessage, setLocalMessage] = useState('');
  const [currentConversation, setCurrentConversation] = useState<any>(null);

  const currentConversationId = searchParams.get('conversationId');

  // Query to get user data including channel type
  const { data: userData } = useQuery(GET_USER, {
    variables: {
      data: {
        id: personId,
        channel_type: 'whatsapp,instagram',
      },
    },
    skip: !personId,
  });

  // Extract channel type from user data
  const channelType = userData?.get_user?.data?.channel_type || '';

  // Helper to handle null from searchParams
  const convIdForSelector = currentConversationId ?? undefined;

  // --- Redux State Selection (Reverted to individual selectors) ---
  const messages: Message[] = useSelector((state: RootState) =>
    selectMessagesByConversationId(state, convIdForSelector),
  );
  const messagesLoading = useSelector((state: RootState) =>
    selectMessagesLoading(state, convIdForSelector),
  );
  const isSendingMessage = useSelector((state: RootState) =>
    selectIsSendingMessage(state, convIdForSelector),
  );
  const messagesError = useSelector((state: RootState) =>
    selectMessagesError(state, convIdForSelector),
  );
  // Check if messages are already loaded (needed for fetch logic)
  const areMessagesLoaded = useSelector(
    (state: RootState) =>
      // Simple check: if messages array for this ID exists and has items, assume loaded
      // More robust check could involve a separate 'loaded' flag in the store
      selectMessagesByConversationId(state, convIdForSelector).length > 0,
  );

  // --- Apollo Queries and Mutations ---
  const { data: activityData, loading: activityLoading } = useQuery(
    GET_USER_ACTIVITY,
    {
      variables: {
        data: {
          user_id: personId,
          agent_id: agentId,
        },
      },
      skip: !personId || !agentId,
    },
  );

  useEffect(() => {
    if (activityData?.response?.data && currentConversationId) {
      const activities = activityData.response.data;
      const foundConversation = activities.find(
        (activity: any) =>
          activity._id === currentConversationId &&
          activity.activity_type === 'CONVERSATIONS',
      );
      setCurrentConversation(foundConversation || null);
    }
  }, [activityData, currentConversationId]);

  const [fetchMessagesQuery] = useLazyQuery<GetMessagesResponse>(GET_MESSAGES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (currentConversationId && data?.response?.data) {
        dispatch(
          addFetchedMessages({
            conversationId: currentConversationId,
            messages: data.response.data,
          }),
        );
      } else if (currentConversationId) {
        dispatch(
          setMessagesError({
            conversationId: currentConversationId,
            error: 'Received invalid data',
          }),
        );
      }
    },
    onError: (error) => {
      if (currentConversationId) {
        dispatch(
          setMessagesError({
            conversationId: currentConversationId,
            error: error.message || 'Failed to fetch messages',
          }),
        );
      }
    },
  });

  const [sendMessageMutation] = useMutation<SendMessageResponse>(SEND_MESSAGE, {
    onCompleted: (data) => {
      if (currentConversationId) {
        dispatch(
          setSendingMessage({
            conversationId: currentConversationId,
            isSending: false,
          }),
        );
        dispatch(
          addMessage({
            conversationId: currentConversationId,
            message: data.send_message.data.message,
          }),
        );
        // Message should be added via websocket if successful
      }
    },
    onError: (error) => {
      if (currentConversationId) {
        dispatch(
          setSendingError({
            conversationId: currentConversationId,
            error: error.message || 'Failed to send message',
          }),
        );
      }
    },
  });

  // --- Effect for Data Fetching ---
  useEffect(() => {
    const convId = currentConversationId;
    if (convId && agentId) {
      // Use the selector result 'areMessagesLoaded' directly
      if (!areMessagesLoaded) {
        dispatch(
          setMessagesLoading({
            conversationId: convId,
            isLoading: true,
          }),
        );
        fetchMessagesQuery({
          variables: {
            data: {
              conversation_id: convId,
            },
          },
        });
      } else {
        // Ensure loading state is false if we skipped fetch but it was somehow true
        if (messagesLoading) {
          dispatch(
            setMessagesLoading({ conversationId: convId, isLoading: false }),
          );
        }
      }
    }
  }, [
    currentConversationId,
    agentId,
    fetchMessagesQuery,
    dispatch,
    areMessagesLoaded,
    messagesLoading,
  ]); // Use selector results in dep array

  // --- Message Sending Logic ---
  const handleSendMessage = useCallback(() => {
    if (
      !localMessage.trim() ||
      !currentConversationId ||
      !agentId ||
      isSendingMessage
    ) {
      return;
    }
    dispatch(
      setSendingMessage({
        conversationId: currentConversationId,
        isSending: true,
      }),
    );
    sendMessageMutation({
      variables: {
        data: {
          conversation_id: currentConversationId,
          message: localMessage.trim(),
        },
      },
    });
    setLocalMessage('');
  }, [
    localMessage,
    currentConversationId,
    agentId,
    isSendingMessage,
    sendMessageMutation,
    dispatch,
    currentConversation,
  ]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  // Scroll to bottom
  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const isMessagingDisabled =
    !currentConversationId || !!currentConversation?.ended_at;
  // Use the direct selector result for loading state calculation
  const loading = messagesLoading || activityLoading;

  if (isSmallDevice && !currentConversationId) {
    return null;
  }

  const handleFileUpload = async (file: File, onComplete?: () => void) => {
    // Validate required IDs
    if (!currentConversationId || !agentId) {
      console.error(
        'Missing required conversation ID or agent ID for file upload',
      );
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('agent_id', agentId);

    try {
      // Set sending state before the request
      dispatch(
        setSendingMessage({
          conversationId: currentConversationId,
          isSending: true,
        }),
      );

      // Make the fetch request to upload the file
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/files/upload`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const result = await response.json();

      // Send the message with media attachment
      await sendMessageMutation({
        variables: {
          data: {
            conversation_id: currentConversationId,
            message: localMessage.trim(),
            media: {
              url: `${import.meta.env.VITE_API_URL}/media/${result.data.url}`,
              type: file.type.split('/')[0],
              fileName: file.name,
              media_id: result.data.url,
            },
          },
        },
      });

      // Clear message and call completion callback
      setLocalMessage('');
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error uploading file or sending message:', error);
      // Reset sending state and show error
      if (currentConversationId) {
        dispatch(
          setSendingError({
            conversationId: currentConversationId,
            error:
              error instanceof Error ? error.message : 'File upload failed',
          }),
        );
      }
    }
  };

  return (
    <div className="w-full">
      <div className="y-2 h-[71vh] w-full overflow-y-scroll bg-[url('./assets/background.png')] bg-[length:30%_auto] px-4 backdrop-opacity-[.15]  lg:h-[79vh] 2xl:h-[82vh]">
        {loading && messages.length === 0 ? (
          <LoadingIndicator />
        ) : (
          <Bubbles ref={messageEndRef} messages={messages} t={t} />
        )}
        {messagesError && (
          <div className="text-error p-2 text-center">
            Error: {messagesError}
          </div>
        )}
      </div>

      <MessageInput
        personChannelId={currentConversation?.person_channel_id}
        message={localMessage}
        setMessage={setLocalMessage}
        sendMessage={handleSendMessage}
        sendInputMessage={handleFileUpload} // Use handleSendMessage for both props
        handleKeyDown={handleKeyDown as (e: KeyboardEvent<Element>) => void}
        isSendingMessage={isSendingMessage}
        isMessagingDisabled={isMessagingDisabled}
        channelType={channelType} // Pass channel type to MessageInput
      />
    </div>
  );
}

export default Messages;
